import React, { useState, useCallback } from 'react';
import { useAppSelector } from 'redux/hooks';
import billingService from 'api/services/billingService';
import type { BillingRequestItem } from 'api/services/billingService';
import Paginator from 'components/Paginator/Paginator';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [200, 201, 204, 400, 401, 403, 404, 429, 500, 502, 503];

function getDefaultFrom(): string {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
}

function getDefaultTo(): string {
    return new Date().toISOString().slice(0, 10);
}

const BillingRequests: React.FC = () => {
    const { sellerUuid } = useAppSelector(({ sellers }) => ({
        sellerUuid: sellers.currentSeller.info.data?.uuid,
    }));

    const [platform, setPlatform] = useState('wb');
    const [status, setStatus] = useState('200');
    const [from, setFrom] = useState(getDefaultFrom());
    const [to, setTo] = useState(getDefaultTo());
    const [limit, setLimit] = useState('');

    const [data, setData] = useState<BillingRequestItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [fetched, setFetched] = useState(false);

    const handleApply = useCallback(async () => {
        if (!sellerUuid) return;

        setLoading(true);
        setError(null);
        setPage(0);

        try {
            const params: any = { seller_uuid: sellerUuid };
            if (platform) params.platform = platform;
            if (status) params.status = parseInt(status);
            if (from) params.from = `${from}T00:00:00Z`;
            if (to) params.to = `${to}T23:59:59Z`;
            if (limit) params.limit = parseInt(limit);

            const response = await billingService.getRequests(params);
            setData(response.data ?? []);
            setFetched(true);
        } catch (e: any) {
            setError(e?.message ?? 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    }, [sellerUuid, platform, status, from, to, limit]);

    const paginatedData = data.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <div className="space-y-4">
            <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                Биллинг: история запросов
            </h1>

            {/* Фильтры */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex flex-wrap gap-4 items-end">
                    {/* Платформа */}
                    <div className="flex flex-col gap-1 min-w-[140px]">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Платформа
                        </label>
                        <select
                            value={platform}
                            onChange={e => setPlatform(e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        >
                            <option value="ozon">Ozon</option>
                            <option value="wb">Wildberries</option>
                        </select>
                    </div>

                    {/* Статус */}
                    <div className="flex flex-col gap-1 min-w-[120px]">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Статус ответа
                        </label>
                        <select
                            value={status}
                            onChange={e => setStatus(e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        >
                            {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    {/* Дата С */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            С какого
                        </label>
                        <input
                            type="date"
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                    </div>

                    {/* Дата По */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            По какое
                        </label>
                        <input
                            type="date"
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                    </div>

                    {/* Лимит */}
                    <div className="flex flex-col gap-1 min-w-[120px]">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Лимит запросов
                        </label>
                        <input
                            type="number"
                            min={1}
                            placeholder="Без лимита"
                            value={limit}
                            onChange={e => setLimit(e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                    </div>

                    {/* Кнопка */}
                    <button
                        onClick={handleApply}
                        disabled={loading || !sellerUuid}
                        className="px-5 py-2 rounded bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                    >
                        {loading ? 'Загрузка...' : 'Применить'}
                    </button>
                </div>
            </div>

            {/* Ошибка */}
            {error && (
                <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-4 py-2">
                    {error}
                </div>
            )}

            {/* Таблица */}
            {fetched && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                                    Запрос
                                </th>
                                <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300 w-28">
                                    Количество
                                </th>
                                <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300 w-36">
                                    Стоимость
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="text-center py-8 text-gray-400 dark:text-gray-500">
                                        Нет данных
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map((item, idx) => (
                                    <tr
                                        key={idx}
                                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                    >
                                        <td className="px-4 py-3 font-mono text-xs text-gray-800 dark:text-gray-200">
                                            {item.query_pattern}
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                                            {item.count.toLocaleString('ru-RU')}
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                                            {item.total_cost.toLocaleString('ru-RU', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 4,
                                            })} ₽
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {data.length > 0 && (
                            <tfoot>
                                <tr className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                                    <td className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                                        Итого
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-200">
                                        {data.reduce((sum, i) => sum + i.count, 0).toLocaleString('ru-RU')}
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-200">
                                        {data.reduce((sum, i) => sum + i.total_cost, 0).toLocaleString('ru-RU', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4,
                                        })} ₽
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>

                    {data.length > PAGE_SIZE && (
                        <div className="flex justify-center py-4 border-t border-gray-100 dark:border-gray-700">
                            <Paginator
                                pageSize={PAGE_SIZE}
                                total={data.length}
                                page={page}
                                callback={p => setPage(p - 1)}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BillingRequests;
